import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, VStack, HStack, Text, Badge, Button, Alert, AlertIcon,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer, Code, Divider, Link,
  Tag, TagLeftIcon, TagLabel, Wrap, useToast,
  Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Checkbox, Textarea, FormControl, FormLabel, Tooltip,
  Input, InputGroup, InputLeftElement,
} from "@chakra-ui/react";
import { CheckCircleIcon, WarningIcon, SearchIcon } from "@chakra-ui/icons";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import {
  fetchAuthHardeningStatus, fetchAuthHardeningUserStatus,
  submitAccessReview, fetchAccessReviewHistory,
} from "../../services/rlsAudit";
import {
  readCache, writeCache, formatAge,
  AUTH_HARDENING_KEY, AUTH_HARDENING_USERS_KEY, ACCESS_REVIEW_HISTORY_KEY,
} from "../../utils/resultCache";
import { reviewPayloadHash } from "../../utils/reviewHash";
import { getNavbarItems } from "../../config/roles";

function fmtDate(iso) {
  if (!iso) return "never recorded";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

const HARDENING_TARGETS = {
  mfa_required: { target: true, label: "MFA required for every account" },
  idle_timeout_minutes: { target: 15, label: "Idle session timeout", unit: " min" },
  absolute_timeout_minutes: { target: 480, label: "Absolute session timeout", unit: " min" },
  lockout_threshold: { target: 5, label: "Failed login lockout threshold", unit: " attempts" },
  lockout_minutes: { target: 30, label: "Lockout duration", unit: " min" },
  min_password_length: { target: 12, label: "Minimum password length", unit: " chars" },
  audit_log_retention_years: { target: 7, label: "Audit log retention", unit: " years" },
};

function compare(actual, target) {
  if (typeof target === "boolean") return actual === target;
  if (typeof target === "number") return Number(actual) === target;
  return false;
}

function PolicyRow({ label, actual, target, unit = "" }) {
  const ok = compare(actual, target);
  return (
    <Tr>
      <Td>
        <Text fontWeight="medium">{label}</Text>
        <Text fontSize="xs" color="gray.500">Target: {String(target)}{unit}</Text>
      </Td>
      <Td>
        <Text fontSize="lg">{String(actual)}{unit}</Text>
      </Td>
      <Td>
        <Badge colorScheme={ok ? "green" : "red"} fontSize="sm">
          {ok ? "OK" : "GAP"}
        </Badge>
      </Td>
    </Tr>
  );
}

const STALE_LABELS = {
  active: { color: "green", text: "active (<30d)" },
  "30d": { color: "yellow", text: "30–60 days" },
  "60d": { color: "orange", text: "60–90 days" },
  "90d": { color: "red", text: "90+ days" },
  never: { color: "gray", text: "never signed in" },
  unknown: { color: "gray", text: "unknown" },
};

function StaleChip({ bucket }) {
  const meta = STALE_LABELS[bucket] || STALE_LABELS.unknown;
  return (
    <Tag size="sm" colorScheme={meta.color} variant="subtle" data-testid={`stale-${bucket}`}>
      <TagLabel>{meta.text}</TagLabel>
    </Tag>
  );
}

export default function UserMgmtPanel() {
  const cached = readCache(AUTH_HARDENING_KEY);
  const cachedUsers = readCache(AUTH_HARDENING_USERS_KEY);
  const cachedHistory = readCache(ACCESS_REVIEW_HISTORY_KEY);
  const [status, setStatus] = useState(cached?.data ?? null);
  const [userStatus, setUserStatus] = useState(cachedUsers?.data ?? null);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usersError, setUsersError] = useState(null);
  const [lastRun, setLastRun] = useState(cached?.savedAt ?? null);
  const [usersLastRun, setUsersLastRun] = useState(cachedUsers?.savedAt ?? null);

  // Stale filter + free-text search (#2 stale detector + UX)
  const [staleFilter, setStaleFilter] = useState("all");
  const [search, setSearch] = useState("");

  // RBAC drawer (#1)
  const [drawerUser, setDrawerUser] = useState(null);

  // Review wizard (#6)
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewHistory, setReviewHistory] = useState(cachedHistory?.data?.rows ?? []);

  const toast = useToast();

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fresh = await fetchAuthHardeningStatus();
      setStatus(fresh);
      setLastRun(new Date().toISOString());
      writeCache(AUTH_HARDENING_KEY, fresh);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const fresh = await fetchAuthHardeningUserStatus();
      setUserStatus(fresh);
      setUsersLastRun(new Date().toISOString());
      writeCache(AUTH_HARDENING_USERS_KEY, fresh);
    } catch (err) {
      setUsersError(err?.message || String(err));
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const refreshHistory = useCallback(async () => {
    try {
      const fresh = await fetchAccessReviewHistory(20);
      setReviewHistory(fresh.rows || []);
      writeCache(ACCESS_REVIEW_HISTORY_KEY, fresh);
    } catch {
      /* non-fatal; the wizard still works without history */
    }
  }, []);

  useEffect(() => {
    refresh();
    refreshUsers();
    refreshHistory();
  }, [refresh, refreshUsers, refreshHistory]);

  const policies = status?.policies ?? {};
  const impersonationEnabled = status?.impersonation_audit_enabled;
  const lastReview = status?.last_quarterly_access_review_at;
  const notes = status?.notes ?? [];

  const users = userStatus?.users ?? [];
  const notHardenedCount = userStatus?.not_hardened_count ?? 0;
  const truncated = Boolean(userStatus?.truncated);
  const staleCounts = userStatus?.stale_counts || {};

  // #2 stale detector — drive filter chips off the backend counts
  const filteredUsers = useMemo(() => {
    const source = users;
    let rows = source;
    if (staleFilter !== "all") {
      rows = rows.filter((u) => (u.stale_bucket || "unknown") === staleFilter);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((u) => {
        const email = (u.email || "").toLowerCase();
        const role = (u.app_role || "").toLowerCase();
        const name = (u.display_name || "").toLowerCase();
        return email.includes(q) || role.includes(q) || name.includes(q);
      });
    }
    return rows;
  }, [users, staleFilter, search]);

  return (
    <VStack align="stretch" spacing={6}>
      <Box>
        <Text fontWeight="bold" mb={1}>Auth Hardening</Text>
        <Text fontSize="sm" color="gray.600" mb={3}>
          P1 #4 of HIPA/SOC2_HIPAA_COMPLIANCE_PLAN.md. The backend surfaces the
          values it enforces; the policies that live in Supabase Auth
          (MFA, lockout, password rules) require operator action via the
          dashboard.
        </Text>
        <HStack mb={3}>
          <Button colorScheme="blue" onClick={refresh} isLoading={loading}>
            Refresh status
          </Button>
          {lastRun && !loading && (
            <Text fontSize="sm" color="gray.600">Checked {formatAge(lastRun)}</Text>
          )}
        </HStack>
        {error && (
          <Alert status="error" borderRadius="md" mb={3}>
            <AlertIcon />
            <Text fontSize="sm">{error}</Text>
          </Alert>
        )}
        {notes.length > 0 && (
          <Alert status="warning" borderRadius="md" mb={3}>
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">Configuration notes</Text>
              <VStack align="stretch" spacing={1} mt={1}>
                {notes.map((n) => <Text key={n} fontSize="sm">• {n}</Text>)}
              </VStack>
            </Box>
          </Alert>
        )}
        <Table size="sm" variant="striped">
          <Thead>
            <Tr>
              <Th>Policy</Th>
              <Th>This environment</Th>
              <Th>Status</Th>
            </Tr>
          </Thead>
          <Tbody>
            {Object.entries(HARDENING_TARGETS).map(([key, meta]) => (
              <PolicyRow
                key={key}
                label={meta.label}
                target={meta.target}
                unit={meta.unit || ""}
                actual={policies[key]}
              />
            ))}
          </Tbody>
        </Table>
      </Box>

      <Divider />

      <Box>
        <HStack justify="space-between" align="flex-end" mb={1}>
          <Box>
            <Text fontWeight="bold">User-level auth hardening</Text>
            <Text fontSize="sm" color="gray.600">
              Every Supabase Auth account and the per-user flags HIPAA needs:
              MFA enrolled, email confirmed, not banned, has signed in at
              least once. A server-side policy that passes can still be
              undermined by individual accounts that skip enrolment.
            </Text>
          </Box>
          <HStack>
            <Button size="sm" colorScheme="blue" variant="outline" onClick={refreshUsers}
                    isLoading={usersLoading} data-testid="refresh-auth-users">
              Refresh users
            </Button>
            {usersLastRun && !usersLoading && (
              <Text fontSize="xs" color="gray.600">Scanned {formatAge(usersLastRun)}</Text>
            )}
          </HStack>
        </HStack>

        {usersError && (
          <Alert status="error" borderRadius="md" mb={3}>
            <AlertIcon />
            <Text fontSize="sm">{usersError}</Text>
          </Alert>
        )}

        {!usersLoading && !usersError && users.length > 0 && notHardenedCount > 0 && (
          <Alert status="warning" borderRadius="md" mb={3} data-testid="auth-users-warning">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">
                {notHardenedCount} of {users.length}{" "}
                account{users.length === 1 ? "" : "s"} {"aren't"} fully hardened
              </Text>
              <Text fontSize="sm">
                Review the rows flagged below and either enroll MFA, confirm the
                email, unban the account, or revoke it. HIPAA Technical
                Safeguards §164.312(d) require person-level authentication
                controls, not just server defaults.
              </Text>
            </Box>
          </Alert>
        )}

        {!usersLoading && !usersError && users.length === 0 && (
          <Alert status="info" borderRadius="md" mb={3}>
            <AlertIcon />
            <Text fontSize="sm">No Supabase Auth users were returned.</Text>
          </Alert>
        )}

        {truncated && (
          <Alert status="info" borderRadius="md" mb={3}>
            <AlertIcon />
            <Text fontSize="sm">
              Showing the first {users.length} accounts. Refine with Supabase
              {" "}<Code fontSize="xs">/auth/v1/admin/users</Code> for the full list.
            </Text>
          </Alert>
        )}

        {/* #2 stale detector — filter chips + counts */}
        {!usersLoading && users.length > 0 && (
          <Wrap mb={3} spacing={2} data-testid="stale-filter-chips">
            <FilterChip
              label={`All (${users.length})`}
              active={staleFilter === "all"}
              onClick={() => setStaleFilter("all")}
              testId="stale-filter-all"
            />
            {Object.entries(STALE_LABELS).map(([key, meta]) => {
              const count = staleCounts[key] || 0;
              if (count === 0) return null;
              return (
                <FilterChip
                  key={key}
                  label={`${meta.text} (${count})`}
                  color={meta.color}
                  active={staleFilter === key}
                  onClick={() => setStaleFilter(key)}
                  testId={`stale-filter-${key}`}
                />
              );
            })}
            <InputGroup size="sm" maxW="240px" ml="auto">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" boxSize={3} />
              </InputLeftElement>
              <Input
                placeholder="Search email / role / name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="users-search"
              />
            </InputGroup>
          </Wrap>
        )}

        <TableContainer>
          <Table size="sm" variant="striped">
            <Thead>
              <Tr>
                <Th>User</Th>
                <Th>Role</Th>
                <Th>Email confirmed</Th>
                <Th>MFA</Th>
                <Th>Last sign-in</Th>
                <Th>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredUsers.map((u) => (
                <UserRow key={u.id || u.email} user={u} onOpen={setDrawerUser} />
              ))}
              {!usersLoading && filteredUsers.length === 0 && users.length > 0 && (
                <Tr>
                  <Td colSpan={6}>
                    <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                      No accounts match the current filter.
                    </Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      <Divider />

      <Box>
        <HStack justify="space-between" align="flex-end" mb={1}>
          <Box>
            <Text fontWeight="bold">Quarterly Access Reviews</Text>
            <Text fontSize="sm" color="gray.600">
              HIPAA requires a documented review of every account's access at
              least every 90 days. The wizard writes a tamper-evident row to
              {" "}<Code fontSize="xs">public.access_reviews</Code> with reviewer
              + user-count + a sha256 seal of the reviewed list.
            </Text>
          </Box>
          <HStack>
            <Button size="sm" colorScheme="purple" onClick={() => setReviewOpen(true)}
                    data-testid="open-review-wizard">
              Start review
            </Button>
            <Button size="sm" variant="outline" onClick={exportEvidencePack}
                    data-testid="export-evidence-pack">
              Export evidence pack
            </Button>
          </HStack>
        </HStack>
        <HStack mt={2}>
          <Text fontSize="md">Last reviewed: <strong>{fmtDate(lastReview)}</strong></Text>
          {lastReview
            ? <Badge colorScheme={isWithin90Days(lastReview) ? "green" : "red"}>{
                isWithin90Days(lastReview) ? "within 90 days" : "overdue"
              }</Badge>
            : <Badge colorScheme="red">not recorded</Badge>}
        </HStack>
        {reviewHistory.length > 0 && (
          <Box mt={3}>
            <Text fontSize="xs" color="gray.600" mb={1}>Recent reviews</Text>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Reviewed at</Th>
                  <Th>Reviewer</Th>
                  <Th isNumeric>Users</Th>
                  <Th isNumeric>Approved</Th>
                  <Th isNumeric>Revoked</Th>
                </Tr>
              </Thead>
              <Tbody>
                {reviewHistory.slice(0, 5).map((row, idx) => (
                  <Tr key={row.payload_hash || idx}>
                    <Td><Text fontSize="xs">{fmtDate(row.reviewed_at)}</Text></Td>
                    <Td><Text fontSize="xs">{row.reviewer_email || row.reviewer_id || "—"}</Text></Td>
                    <Td isNumeric>{row.user_count}</Td>
                    <Td isNumeric>{row.approved}</Td>
                    <Td isNumeric>{row.revoked}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>

      <Divider />

      <Box>
        <Text fontWeight="bold">View-as-user audit linkage</Text>
        <Text fontSize="sm" color="gray.600" mb={3}>
          Impersonation sessions are recorded in <Code fontSize="xs">public.impersonation_audit</Code>
          {" "}— a row per /admin/impersonate-user call. Open the
          "Logs Review / Auditing" tab to query the live session history.
        </Text>
        <HStack>
          <Badge colorScheme={impersonationEnabled ? "green" : "red"}>
            impersonation_audit {impersonationEnabled ? "enabled" : "disabled"}
          </Badge>
          {status?.supabase_admin_url && (
            <Link href={status.supabase_admin_url} isExternal color="blue.600" fontSize="sm">
              Supabase Auth settings →
            </Link>
          )}
        </HStack>
      </Box>

      <RbacDrawer user={drawerUser} onClose={() => setDrawerUser(null)} />
      <ReviewWizard
        isOpen={reviewOpen}
        users={users}
        onClose={() => setReviewOpen(false)}
        onSubmitted={async () => {
          await refresh();
          await refreshHistory();
        }}
      />
    </VStack>
  );

  // ===== helpers =====

  async function exportEvidencePack() {
    try {
      const zip = new JSZip();
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const policies = status?.policies || {};
      const summary = {
        generated_at: new Date().toISOString(),
        last_reviewed_at: lastReview || null,
        not_hardened_count: notHardenedCount,
        user_count: users.length,
        truncated: truncated || false,
        stale_counts: staleCounts,
        policies,
        notes: status?.notes || [],
        impersonation_audit_enabled: Boolean(impersonationEnabled),
      };
      zip.file("summary.json", JSON.stringify(summary, null, 2));
      zip.file("auth-users.json", JSON.stringify(userStatus || {}, null, 2));
      zip.file("users.csv", usersToCsv(users));
      zip.file("policies.csv", policiesToCsv(policies));
      zip.file("stale-buckets.csv", staleToCsv(staleCounts));
      zip.file("README.txt",
        "HIPAA User Management evidence pack\n" +
        `Generated: ${summary.generated_at}\n` +
        `Users: ${users.length} (${notHardenedCount} not hardened)\n` +
        `Last quarterly review: ${lastReview || "never"}\n` +
        "Files:\n" +
        "  summary.json          — headline posture snapshot\n" +
        "  policies.csv          — server-enforced policy values vs targets\n" +
        "  auth-users.json       — full Supabase Auth user dump with hardening flags\n" +
        "  users.csv             — flattened user table for auditors\n" +
        "  stale-buckets.csv     — sign-in recency histogram\n"
      );
      const blob = await zip.generateAsync({ type: "blob" });
      saveAs(blob, `hipaa-user-mgmt-${stamp}.zip`);
      toast({
        title: "Evidence pack downloaded",
        description: `hipaa-user-mgmt-${stamp}.zip`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Export failed",
        description: err?.message || String(err),
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    }
  }
}

function isWithin90Days(iso) {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  return (Date.now() - t) <= 90 * 24 * 3600 * 1000;
}

function FilterChip({ label, active, onClick, color = "blue", testId }) {
  return (
    <Tag
      as="button"
      type="button"
      onClick={onClick}
      cursor="pointer"
      size="sm"
      variant={active ? "solid" : "subtle"}
      colorScheme={color}
      data-testid={testId}
    >
      <TagLabel>{label}</TagLabel>
    </Tag>
  );
}

function Chip({ ok, label }) {
  return (
    <Tag size="sm" colorScheme={ok ? "green" : "red"} variant="subtle" data-testid={`chip-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <TagLeftIcon as={ok ? CheckCircleIcon : WarningIcon} />
      <TagLabel>{label}</TagLabel>
    </Tag>
  );
}

function UserRow({ user, onOpen }) {
  const ok = Boolean(user.auth_hardened);
  const reasons = [];
  if (!user.email_confirmed) reasons.push("email not confirmed");
  if (!user.mfa_enrolled) reasons.push("no MFA");
  if (user.is_banned) reasons.push("banned");
  if (!user.has_signed_in) reasons.push("never signed in");
  const handleOpen = () => { if (onOpen) onOpen(user); };
  return (
    <Tr bg={ok ? undefined : "red.50"} _hover={{ bg: "blue.50", cursor: "pointer" }}
        onClick={handleOpen} data-testid={`user-row-${user.id || user.email}`}>
      <Td>
        <Text fontWeight="medium">{user.email || "(no email)"}</Text>
        <Text fontSize="xs" color="gray.500">
          created {fmtDate(user.created_at)}
        </Text>
        <StaleChip bucket={user.stale_bucket || "unknown"} />
      </Td>
      <Td>
        {user.app_role
          ? <Badge colorScheme="purple" variant="subtle">{user.app_role}</Badge>
          : <Text fontSize="xs" color="gray.500">—</Text>}
        {user.schemas && user.schemas.length > 0 && (
          <Wrap mt={1}>
            {user.schemas.map((s) => (
              <Tag key={s} size="sm" variant="outline">{s}</Tag>
            ))}
          </Wrap>
        )}
      </Td>
      <Td>
        <Chip ok={user.email_confirmed} label={user.email_confirmed ? "verified" : "unverified"} />
      </Td>
      <Td>
        <Chip
          ok={user.mfa_enrolled}
          label={user.mfa_enrolled ? `enrolled (${user.mfa_factor_count})` : "missing"}
        />
      </Td>
      <Td>
        <Text fontSize="sm">{fmtDate(user.last_sign_in_at)}</Text>
        {user.is_banned && user.banned_until && (
          <Text fontSize="xs" color="red.600">
            banned until {fmtDate(user.banned_until)}
          </Text>
        )}
      </Td>
      <Td>
        <Badge colorScheme={ok ? "green" : "red"} fontSize="sm">
          {ok ? "HARDENED" : "GAP"}
        </Badge>
        {!ok && reasons.length > 0 && (
          <Wrap mt={1}>
            {reasons.map((r) => (
              <Tag key={r} size="sm" colorScheme="orange" variant="outline">
                {r}
              </Tag>
            ))}
          </Wrap>
        )}
      </Td>
    </Tr>
  );
}

// ===== RBAC drawer (#1) =====

function RbacDrawer({ user, onClose }) {
  const open = Boolean(user);
  const role = user?.app_role || null;
  const schemas = user?.schemas || [];
  const navItems = useMemo(() => {
    if (!role) return [];
    try { return getNavbarItems(role); }
    catch { return []; }
  }, [role]);
  return (
    <Drawer isOpen={open} onClose={onClose} placement="right" size="md">
      <DrawerOverlay />
      <DrawerContent data-testid="rbac-drawer">
        <DrawerCloseButton />
        <DrawerHeader>{user?.email || "Account"}</DrawerHeader>
        <DrawerBody>
          {!user ? null : (
            <VStack align="stretch" spacing={4}>
              <Box>
                <Text fontSize="xs" color="gray.500">Display name</Text>
                <Text>{user.display_name || "—"}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">Supabase Auth id</Text>
                <Code fontSize="xs" wordBreak="break-all">{user.id}</Code>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">Created</Text>
                <Text>{fmtDate(user.created_at)}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">Last sign-in</Text>
                <Text>{fmtDate(user.last_sign_in_at)}</Text>
              </Box>
              <Divider />
              <Box>
                <Text fontSize="xs" color="gray.500">App role</Text>
                {role
                  ? <Badge colorScheme="purple">{role}</Badge>
                  : <Text fontSize="sm" color="gray.500">No role assigned in public.users — account has zero application access.</Text>}
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">Schemas / clinics</Text>
                {schemas.length > 0
                  ? <Wrap>{schemas.map((s) => <Tag key={s} variant="outline">{s}</Tag>)}</Wrap>
                  : <Text fontSize="sm" color="gray.500">None</Text>}
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">Effective nav (from roles.json)</Text>
                {navItems.length > 0
                  ? <Wrap>{navItems.map((i) => <Tag key={i.pageKey} colorScheme="blue" variant="subtle">{i.label}</Tag>)}</Wrap>
                  : <Text fontSize="sm" color="gray.500">No navbar items unlocked by this role.</Text>}
              </Box>
              <Divider />
              <Box>
                <Text fontSize="xs" color="gray.500">Hardening</Text>
                <Wrap>
                  <Chip ok={user.email_confirmed} label={user.email_confirmed ? "email verified" : "email unverified"} />
                  <Chip ok={user.mfa_enrolled} label={user.mfa_enrolled ? `MFA (${user.mfa_factor_count})` : "no MFA"} />
                  <Chip ok={!user.is_banned} label={user.is_banned ? "banned" : "not banned"} />
                  <Chip ok={user.has_signed_in} label={user.has_signed_in ? "has signed in" : "never signed in"} />
                </Wrap>
                <Box mt={2}><StaleChip bucket={user.stale_bucket || "unknown"} /></Box>
              </Box>
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

// ===== Review wizard (#6) =====

function ReviewWizard({ isOpen, users, onClose, onSubmitted }) {
  const toast = useToast();
  const [selected, setSelected] = useState([]);
  const [revoked, setRevoked] = useState([]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState("");

  // Reset state when the wizard opens for a new review session.
  const firstOpenRef = useRef(true);
  useEffect(() => {
    if (isOpen && firstOpenRef.current) {
      setSelected(users.map((u) => u.id).filter(Boolean));
      setRevoked([]);
      setNotes("");
      setFilter("");
      firstOpenRef.current = false;
    }
    if (!isOpen) firstOpenRef.current = true;
  }, [isOpen, users]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => (u.email || "").toLowerCase().includes(q));
  }, [users, filter]);

  const approved = selected.filter((id) => !revoked.includes(id));
  const approvedCount = approved.length;
  const revokedCount = revoked.length;

  const toggle = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    setRevoked((prev) => prev.filter((x) => x !== id));
  };
  const toggleRevoked = (id, checked) => {
    if (!selected.includes(id)) {
      // must be selected to mark as revoked
      if (checked) {
        setSelected((prev) => [...prev, id]);
        setRevoked((prev) => [...prev, id]);
      }
      return;
    }
    setRevoked((prev) => checked ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id));
  };

  const submit = async () => {
    if (selected.length === 0) {
      toast({ title: "Pick at least one account", status: "warning", duration: 4000, isClosable: true });
      return;
    }
    setSubmitting(true);
    try {
      const hash = await reviewPayloadHash({ reviewedIds: selected, notes });
      const res = await submitAccessReview({
        reviewed_user_ids: selected,
        approved: approved,
        revoked: revoked,
        notes,
        payload_hash: hash,
      });
      const reviewedAt = res?.review?.reviewed_at || new Date().toISOString();
      toast({
        title: "Access review recorded",
        description: `${approvedCount} approved, ${revokedCount} revoked — stamped ${fmtDate(reviewedAt)}`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      if (onSubmitted) onSubmitted();
      onClose();
    } catch (err) {
      toast({
        title: "Submission failed",
        description: err?.message || String(err),
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent data-testid="review-wizard">
        <ModalHeader>Quarterly access review</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={3}>
            <Text fontSize="sm" color="gray.600">
              Tick each account you reviewed. Untick accounts you cannot vouch for;
              tick "revoke" on accounts to remove. A sha256 of the reviewed list
              + notes is sent as <Code fontSize="xs">payload_hash</Code> for tamper-evidence.
            </Text>
            <HStack>
              <Badge colorScheme="green">{approvedCount} approved</Badge>
              <Badge colorScheme="red">{revokedCount} revoked</Badge>
              <Badge colorScheme="gray">{users.length - selected.length} skipped</Badge>
              <Input size="sm" placeholder="Filter…" value={filter} onChange={(e) => setFilter(e.target.value)}
                     maxW="200px" ml="auto" data-testid="review-filter" />
            </HStack>
            <TableContainer maxH="320px" overflowY="auto">
              <Table size="sm">
                <Thead position="sticky" top={0} bg="white" zIndex={1}>
                  <Tr>
                    <Th>Reviewed</Th>
                    <Th>Revoke</Th>
                    <Th>User</Th>
                    <Th>Role</Th>
                    <Th>MFA</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filtered.map((u) => (
                    <Tr key={u.id || u.email}>
                      <Td>
                        <Checkbox isChecked={selected.includes(u.id)} onChange={() => toggle(u.id)}
                                  data-testid={`review-check-${u.id}`} />
                      </Td>
                      <Td>
                        <Checkbox isChecked={revoked.includes(u.id)}
                                  onChange={(e) => toggleRevoked(u.id, e.target.checked)}
                                  colorScheme="red"
                                  isDisabled={!selected.includes(u.id)}
                                  data-testid={`review-revoke-${u.id}`} />
                      </Td>
                      <Td>
                        <Text fontSize="sm">{u.email || "(no email)"}</Text>
                        <Text fontSize="xs" color="gray.500">{fmtDate(u.last_sign_in_at) || "never signed in"}</Text>
                      </Td>
                      <Td>
                        {u.app_role
                          ? <Badge colorScheme="purple">{u.app_role}</Badge>
                          : <Text fontSize="xs" color="gray.500">—</Text>}
                      </Td>
                      <Td>
                        <Chip ok={u.mfa_enrolled} label={u.mfa_enrolled ? `MFA (${u.mfa_factor_count})` : "no MFA"} />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            <FormControl>
              <FormLabel fontSize="sm">Reviewer notes</FormLabel>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                        placeholder="Findings, exceptions, follow-ups…"
                        size="sm" rows={3} maxLength={4000}
                        data-testid="review-notes" />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={submitting}>Cancel</Button>
          <Tooltip label="Writes a tamper-evident row to public.access_reviews">
            <Button colorScheme="purple" onClick={submit} isLoading={submitting}
                    loadingText="Submitting" data-testid="review-submit">
              Submit review ({approvedCount + revokedCount})
            </Button>
          </Tooltip>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ===== CSV helpers for the evidence pack (#12) =====

function csvEscape(value) {
  if (value == null) return "";
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function usersToCsv(users) {
  const headers = [
    "id", "email", "display_name", "app_role", "schemas",
    "created_at", "last_sign_in_at", "email_confirmed_at", "banned_until",
    "mfa_enrolled", "mfa_factor_count", "email_confirmed",
    "is_banned", "has_signed_in", "auth_hardened", "stale_bucket",
  ];
  const rows = [headers.join(",")];
  for (const u of users) {
    rows.push([
      u.id, u.email, u.display_name, u.app_role, (u.schemas || []).join("|"),
      u.created_at, u.last_sign_in_at, u.email_confirmed_at, u.banned_until,
      u.mfa_enrolled, u.mfa_factor_count, u.email_confirmed,
      u.is_banned, u.has_signed_in, u.auth_hardened, u.stale_bucket,
    ].map(csvEscape).join(","));
  }
  return rows.join("\n");
}

function policiesToCsv(policies) {
  const rows = ["policy,actual,target"];
  for (const [key, meta] of Object.entries(HARDENING_TARGETS)) {
    rows.push([key, policies[key], meta.target].map(csvEscape).join(","));
  }
  return rows.join("\n");
}

function staleToCsv(staleCounts) {
  const rows = ["bucket,count"];
  for (const [bucket, count] of Object.entries(staleCounts || {})) {
    rows.push([bucket, count].map(csvEscape).join(","));
  }
  return rows.join("\n");
}
